import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { toast } from 'react-toastify';
import useConfirm from '../../hooks/useConfirm';
import useGetJoinRequests from '../../hooks/joinRequests/useGetJoinRequests';
import useReviewJoinRequest from '../../hooks/joinRequests/useReviewJoinRequest';
import useLoadingModal from '../../hooks/global/useLoadingModal';
import useUser from '../../hooks/global/useUser';
import { LoadedUser } from '../../interfaces';
import { refetchJoinRequests } from '../../services/joinRequests';

interface Props {
  onSuccess: () => void;
}

function PendingJoinRequests({ onSuccess }: Props) {
  const theme = useTheme();
  const confirm = useConfirm();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();
  const { user } = useUser<LoadedUser>();

  const { data: joinRequests, isLoading } = useGetJoinRequests();
  const { mutateAsync: reviewJoinRequest } = useReviewJoinRequest();

  const handleApprove = React.useCallback(
    async (requestId: string, artistName: string) => {
      const confirmed = await confirm({
        title: 'Approve Request',
        message: `Are you sure you want to approve ${artistName}'s request to join?`,
        positiveText: 'Approve',
      });
      if (!confirmed) return;
      try {
        openLoadingModal({ header: 'Approving Request', message: '' });
        await reviewJoinRequest({ requestId, payload: { status: 'APPROVED' } });
        await refetchJoinRequests(user.business.id);
        onSuccess();
        toast.success(`${artistName} has been added to your team`);
      } catch {
        toast.error('Error approving request');
      } finally {
        closeLoadingModal();
      }
    },
    [
      confirm,
      reviewJoinRequest,
      user.business.id,
      onSuccess,
      openLoadingModal,
      closeLoadingModal,
    ],
  );

  const handleReject = React.useCallback(
    async (requestId: string, artistName: string) => {
      const confirmed = await confirm({
        title: 'Decline Request',
        message: `Are you sure you want to decline ${artistName}'s request to join?`,
        positiveText: 'Decline',
      });
      if (!confirmed) return;
      try {
        openLoadingModal({ header: 'Declining Request', message: '' });
        await reviewJoinRequest({ requestId, payload: { status: 'REJECTED' } });
        await refetchJoinRequests(user.business.id);
        onSuccess();
        toast.success('Request declined');
      } catch {
        toast.error('Error declining request');
      } finally {
        closeLoadingModal();
      }
    },
    [
      confirm,
      reviewJoinRequest,
      user.business.id,
      onSuccess,
      openLoadingModal,
      closeLoadingModal,
    ],
  );

  if (!joinRequests?.length) return null;

  return (
    <Box
      sx={{
        mb: 3,
        borderRadius: '10px',
        border: '1px solid #edeeef',
        overflow: 'hidden',
      }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        sx={{
          px: 4,
          py: 2.5,
          borderBottom: '1px solid #edeeef',
        }}>
        <Typography variant="h3" sx={{ mb: 0 }}>
          Pending Join Requests
        </Typography>
        <Chip
          label={joinRequests.length}
          size="small"
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: 'common.white',
            height: 20,
            '& .MuiChip-label': { px: 1 },
            typography: 'overline',
          }}
        />
      </Stack>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        joinRequests.map((request, index) => {
          const artistName =
            request.employee.displayName ||
            `${request.employee.user.firstName} ${request.employee.user.lastName}`.trim();
          const initials = artistName
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
          const requestDate = new Date(request.createdAt).toLocaleDateString(
            'en-US',
            { month: 'short', day: 'numeric' },
          );

          return (
            <Box
              key={request.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 4,
                py: 2,
                borderBottom:
                  index < joinRequests.length - 1
                    ? '1px solid #edeeef'
                    : 'none',
              }}>
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Avatar
                  src={request.employee.profileImage ?? undefined}
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: theme.palette.darkGrey.main,
                    typography: 'h5',
                    fontWeight: 700,
                  }}>
                  {initials}
                </Avatar>
                <Stack gap={0.25}>
                  <Typography variant="h5">{artistName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Requested {requestDate}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleReject(request.id, artistName)}
                  sx={{
                    borderColor: theme.palette.darkerLightGrey.main,
                    color: 'text.secondary',
                    '&:hover': {
                      borderColor: theme.palette.outlineGrey.main,
                      backgroundColor: 'transparent',
                    },
                  }}>
                  Decline
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  className="primary"
                  onClick={() => handleApprove(request.id, artistName)}>
                  Approve
                </Button>
              </Stack>
            </Box>
          );
        })
      )}
    </Box>
  );
}

export default PendingJoinRequests;
